import React, { useCallback, useEffect, useState } from 'react'

import { getNewUuid } from '../../../utils'
import { AssetWithDocumentProps, FileWithError } from '../UploadArea.types'
import { getImageDimensions } from '../UploadArea.utils'

export interface UseManageAssetsStateProps {
  files: AssetWithDocumentProps[]
  filesUploadProgress: Record<string, number | any>
  filesWithError: FileWithError
}

export function useManageAssets(
  targetId: string,
  files: AssetWithDocumentProps[],
  createAsset: (
    targetId: string,
    file: File,
    fileId: string,
    progressFileFn: (id: string) => (progress: number) => void,
    width?: number,
    height?: number,
  ) => Promise<any>,
  // required to update files for Controlled component
  forceResetFiles?: unknown,
) {
  const [state, setState] = useState<UseManageAssetsStateProps>({
    files,
    filesUploadProgress: {},
    filesWithError: {},
  })

  useEffect(() => {
    setState({
      files,
      filesUploadProgress: {},
      filesWithError: {},
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceResetFiles])

  const setFileUploadProgress = (fileId: string) => (progress: number) => {
    setState(prev => ({
      ...prev,
      filesUploadProgress: {
        ...prev.filesUploadProgress,
        [fileId]: progress,
      },
    }))
  }

  const saveFilesUri = useCallback(
    async (newFiles: Array<File>) => {
      const filesDetailed = await Promise.all(
        newFiles.map(async it => {
          const localUrl = URL.createObjectURL(it)
          const dimensions = await getImageDimensions(localUrl).catch(() => undefined)

          return {
            id: getNewUuid(),
            size: it.size,
            url: localUrl,
            fileName: it.name,
            createdAt: new Date(),
            documentId: getNewUuid(),
            disabled: false,
            dimensions,
          }
        }),
      )

      setState(prev => ({
        ...prev,
        files: [...prev.files, ...filesDetailed],
      }))

      const filesUploadedPromise = filesDetailed.map(async (it, key) => {
        const result = await createAsset(
          targetId,
          newFiles[key],
          it.id,
          setFileUploadProgress,
          it.dimensions?.width,
          it.dimensions?.height,
        )

        setState(prev => {
          const currentFiles = Array.from(prev.files)
          const fileIndex = currentFiles.findIndex(file => file.id === it.id)
          const newFileIndex = fileIndex !== -1 ? fileIndex : currentFiles.length
          const updatedFiles = currentFiles.map((file, index) => {
            if (index === newFileIndex) {
              return {
                ...currentFiles[newFileIndex],
                url: result?.localUrl,
                id: result?.assetId,
                documentId: result?.documentId,
                description: result?.description,
                disabled: false,
              }
            }

            return file
          })

          return {
            ...prev,
            files: updatedFiles,
          }
        })

        return {
          url: result?.localUrl,
          id: result?.assetId,
          documentId: result?.documentId,
          description: result?.description,
          disabled: false,
        }
      })

      const results = await Promise.allSettled(filesUploadedPromise)

      const newErrors: FileWithError = {}
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const fileDetail = filesDetailed[index]
          const originalFile = newFiles[index]
          newErrors[fileDetail.id] = originalFile
        }
      })

      if (Object.keys(newErrors).length > 0) {
        setState(prev => ({
          ...prev,
          filesWithError: { ...prev.filesWithError, ...newErrors },
        }))
      }
    },
    [targetId, createAsset],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      saveFilesUri(Array.from(e.target.files || []))
    },
    [saveFilesUri],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault()
      saveFilesUri(Array.from(e.dataTransfer.files))
    },
    [saveFilesUri],
  )

  const handleRetryFile = useCallback(
    (fileToUpload: File) => {
      const filesWithError = { ...state.filesWithError }
      const updatedFilesWithError = Object.keys(filesWithError).reduce((accFiles, fileKey) => {
        const currentFile = filesWithError[fileKey]

        if (currentFile?.name === fileToUpload.name) {
          setFileUploadProgress(fileKey)
          return accFiles
        }

        return { ...accFiles, [fileKey]: currentFile }
      }, {})

      setState(prev => ({
        ...prev,
        files: prev.files.filter(file => file.fileName !== fileToUpload.name),
        filesWithError: updatedFilesWithError,
      }))

      saveFilesUri([fileToUpload])
    },

    [state.filesWithError, saveFilesUri],
  )

  const sortFiles = useCallback(
    (sourceIndex: number, destinationIndex?: number) => {
      if (typeof destinationIndex !== 'number') return

      const items = Array.from(state.files)

      const [removed] = items.splice(sourceIndex, 1)
      items.splice(destinationIndex, 0, removed)

      setState(prev => ({ ...prev, files: items }))
    },
    [state.files],
  )

  const setFiles = useCallback((newFiles: AssetWithDocumentProps[]) => {
    setState(prev => ({ ...prev, newFiles }))
  }, [])

  const removeFile = (index: number) => {
    const localFiles = [...state.files]
    const filesWithError = { ...state.filesWithError }

    const fileToRemove = localFiles.filter((_, key) => key === index)[0]
    const updatedFilesWithError = Object.keys(filesWithError).reduce((accFiles, fileKey) => {
      const currentFile = filesWithError[fileKey]

      if (currentFile?.name === fileToRemove.fileName) {
        setFileUploadProgress(fileKey)
        return accFiles
      }

      return { ...accFiles, currentFile }
    }, {})

    setState(prev => ({
      ...prev,
      files: prev.files.filter((_, key) => key !== index),
      filesWithError: updatedFilesWithError,
    }))
  }

  return {
    handleInputChange,
    handleDrop,
    handleRetryFile,
    sortFiles,
    setFiles,
    removeFile,
    ...state,
  }
}
